import React from 'react';
import { Input } from '@@/ui/primitives';
import { usePageLocations } from './hooks/usePageLocations';
import { PageLocationsChip } from './PageLocationsChip';

interface LocationsFieldProps {
  locations: string[];
  locationInput: string;
  onLocationAdd: (location: string) => void;
  onLocationRemove: (index: number) => void;
  onLocationInputChange: (value: string) => void;
}

export const LocationsField: React.FC<LocationsFieldProps> = ({
  locations,
  locationInput,
  onLocationAdd,
  onLocationRemove,
  onLocationInputChange
}) => {
  const { locations: availableLocations, isLoading: loadingLocations } = usePageLocations();
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && locationInput.trim()) {
      const trimmedInput = locationInput.trim();
      // Only add if it's a valid option from backend
      const valid = availableLocations.includes(trimmedInput);
      if (valid && !locations.includes(trimmedInput)) {
        onLocationAdd(trimmedInput);
      }
    }
  };

  return (
    <div className='w-full min-h-[120px] py-2 border border-[#E5E7EB] rounded-lg bg-white flex flex-col'>
      {/* Input Field */}
      <Input
        type='text'
        value={locationInput}
        onChange={(e) => onLocationInputChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder='Typeahead to select a page'
        className='border-0 shadow-none focus-visible:ring-0 text-base md:text-sm placeholder:text-base md:placeholder:text-sm border-b border-[#E5E7EB] rounded-none pb-2 px-2.5 w-full'
        style={{ fontSize: '14px' }}
      />

      {/* Chips Container */}
      {locations.length > 0 && (
        <div className='flex flex-wrap gap-1.5 pt-2 px-2'>
          {locations.map((location, index) => (
            <PageLocationsChip key={index} label={location} onRemove={() => onLocationRemove(index)} />
          ))}
        </div>
      )}

      {/* Dropdown suggestions */}
      {locationInput && (
        <div className='px-2 max-h-[150px] overflow-y-auto'>
          {loadingLocations ? (
            <div className='px-3 py-2 text-[13px] text-gray-500'>Loading…</div>
          ) : (
            availableLocations
              .filter((opt) => opt.toLowerCase().includes(locationInput.toLowerCase()))
              .filter((opt) => !locations.includes(opt))
              .map((option) => (
                <div
                  key={option}
                  onClick={() => {
                    onLocationAdd(option);
                    onLocationInputChange('');
                  }}
                  className='px-3 py-2 cursor-pointer border-b border-[#E5E7EB] text-[13px] hover:bg-gray-50'
                >
                  {option}
                </div>
              ))
          )}
        </div>
      )}
    </div>
  );
};
